import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { useLingui } from "@lingui/react/macro";
import { AnimatePresence, m } from "framer-motion";
import { X } from "lucide-react";

import { cn } from "@pontimed/ui";
import {
  backdropVariants,
  bottomSheetVariants,
  dialogVariants,
  sheetClasses,
  sheetWidthClass,
  useBodyScrollLock,
  useDragToDismiss,
  useIsDesktop,
  useSheetStackSlot,
} from "@pontimed/ui/sheet-kit";

export type ResponsiveSheetWidth = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "full";

// Each mount of the history-sentinel effect claims a unique id so its
// deferred cleanup only pops a sentinel IT pushed. Without the id, a
// StrictMode remount (or a sibling sheet opening in the same commit) leaves
// a *new* sentinel on top that the stale cleanup would history.back() —
// firing a popstate the fresh listener treats as a close request. That was
// exactly the "consent gate flashes then redirects to dashboard" bug.
let sheetSentinelSeq = 0;

interface SheetSentinelState {
  pontimedSheet?: boolean;
  pontimedSheetId?: number;
}

export type ResponsiveSheetDesktopMode = "dialog" | "float-right";

export interface ResponsiveSheetProps {
  open: boolean;
  onClose: () => void;

  title?: ReactNode;
  description?: ReactNode;
  headerActions?: ReactNode;
  headerSlot?: ReactNode;

  footer?: ReactNode;

  width?: ResponsiveSheetWidth;
  desktop?: ResponsiveSheetDesktopMode;

  hideHandle?: boolean;
  hideCloseButton?: boolean;
  showBackdrop?: boolean;

  /**
   * When `false`, user-initiated dismissal is blocked: the X button is
   * hidden, ESC is ignored, backdrop clicks don't close, drag-to-dismiss is
   * disabled, and the drag handle is suppressed. Programmatic close (via
   * the `onClose` prop being called from the consumer) still works. Use for
   * blocking gates (e.g. auth modal opened by a route guard) where the user
   * must complete the flow or navigate away. Defaults to `true`.
   */
  dismissible?: boolean;

  /**
   * Render mobile (< sm) as a full-screen panel — `inset-0`, `h-[100dvh]`,
   * no rounded top, no drag handle. Used by sheets where a bottom-sheet
   * peek doesn't make sense (e.g. multi-section filter form). Implies
   * `hideHandle` and disables drag-to-dismiss on mobile.
   */
  mobileFullScreen?: boolean;

  /**
   * Disable the built-in history sentinel + popstate-to-close handling.
   * Set true when the consumer drives `open` from the URL (e.g. via a
   * search param). The sentinel adds an extra history entry that conflicts
   * with URL-driven sheets — back-button close then needs two taps and
   * leaves stale entries. With this flag, browser back changes the URL
   * directly and the consumer reacts via its own URL→state binding.
   */
  disableHistory?: boolean;

  ariaLabel?: string;
  closeLabel?: string;

  panelClassName?: string;
  bodyClassName?: string;

  children: ReactNode;
}

/**
 * apps/app shell for the shared ResponsiveSheet contract. Same tokens,
 * classes, animations and drag math as apps/web (sourced from
 * `@pontimed/ui/sheet-kit`) — only the focus / portal plumbing differs:
 * apps/app already runs base-ui Dialog elsewhere, so we lean on
 * `DialogPrimitive` for focus trap + body scroll lock + portal management
 * and reach for `framer-motion` solely for sheet m.
 *
 * On mobile (< sm) renders a bottom-sheet with drag-to-dismiss; on desktop
 * renders a centered scaled-fade dialog. ESC + outside-click + drag past
 * threshold all call `onClose`.
 */
export function ResponsiveSheet({
  open,
  onClose,
  title,
  description,
  headerActions,
  headerSlot,
  footer,
  width = "md",
  desktop = "dialog",
  hideHandle = false,
  hideCloseButton = false,
  showBackdrop = true,
  mobileFullScreen = false,
  disableHistory = false,
  dismissible = true,
  ariaLabel,
  closeLabel,
  panelClassName,
  bodyClassName,
  children,
}: ResponsiveSheetProps) {
  const { t } = useLingui();
  const resolvedCloseLabel = closeLabel ?? t`Close`;
  const isDesktop = useIsDesktop();
  const panelRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const { backdropZ, panelZ } = useSheetStackSlot(open);

  // Stable accessor for the latest onClose so popstate / focus-trap effects
  // only re-run on `open` toggles. Inline `onClose={() => ...}` consumers
  // would otherwise cause history-sentinel thrashing every render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useBodyScrollLock(open);

  // Bfcache restoration: BfcacheGuard fires this event from the root when
  // the page is brought back from the back-forward cache. Unconditionally
  // close so consumer state stays in sync with the just-cleared DOM.
  useEffect(() => {
    function onRestored() {
      onCloseRef.current();
    }
    window.addEventListener("pontimed:bfcache-restored", onRestored);
    return () => window.removeEventListener("pontimed:bfcache-restored", onRestored);
  }, []);

  // Browser-back-button = close. Pushes a sentinel history entry on open,
  // consumes it on `popstate` (mirrors apps/web shell).
  useEffect(() => {
    if (!open || typeof window === "undefined") return;
    if (disableHistory) return;

    let backWasUsed = false;
    // If the top entry is already a pontimedSheet sentinel (StrictMode
    // remount, or a sibling sheet that closed in this same commit whose
    // deferred pop hasn't landed yet), take it over via replaceState
    // instead of stacking a second one. The stale owner's deferred cleanup
    // below sees a foreign id and skips its history.back() — the popstate
    // that used to close route-gate sheets (e.g. the health-consent gate)
    // the instant they mounted.
    const sentinelId = ++sheetSentinelSeq;
    const stateBefore = window.history.state as SheetSentinelState | null;
    if (stateBefore?.pontimedSheet === true) {
      window.history.replaceState({ pontimedSheet: true, pontimedSheetId: sentinelId }, "");
    } else {
      window.history.pushState({ pontimedSheet: true, pontimedSheetId: sentinelId }, "");
    }

    function onPopState() {
      backWasUsed = true;
      onCloseRef.current();
    }
    window.addEventListener("popstate", onPopState);

    return () => {
      window.removeEventListener("popstate", onPopState);
      // Defer the sentinel pop past the current task queue so any
      // navigation that fired alongside the close (e.g., a click handler
      // doing `onClose(); navigate(...)` for a URL-driven sibling sheet)
      // gets a chance to flush its pushState first. TanStack Router flushes
      // its pushState in a microtask, while the cleanup itself runs
      // synchronously in the React commit — without this defer, the
      // synchronous `history.back()` here pops history BEFORE TanStack has
      // pushed the new URL on top of the sentinel. The popstate then races
      // with TanStack's pushState and silently reverts the just-set search
      // params, which made URL-driven sheets like AddModals (`?add=...`)
      // appear and instantly close. setTimeout(0) lands AFTER all
      // microtasks (including TanStack's flush2), so by the time we read
      // `history.state` here, pontimedSheet has been buried under the new
      // entry and we correctly skip the redundant pop.
      setTimeout(() => {
        if (backWasUsed) return;
        if (typeof document === "undefined" || document.visibilityState !== "visible") return;
        const stateNow = window.history.state as SheetSentinelState | null;
        // Only pop a sentinel this effect instance owns — a different id
        // means a newer mount (StrictMode replay / sibling sheet) adopted
        // the slot and the sheet is still open.
        if (stateNow?.pontimedSheet !== true || stateNow.pontimedSheetId !== sentinelId) {
          return;
        }
        window.history.back();
      }, 0);
    };
  }, [open, disableHistory]);

  // ESC + Tab trap. base-ui Dialog handles modal focus trap on desktop,
  // but the bottom-sheet renders outside that primitive (createPortal),
  // so we reproduce minimal trap logic to keep parity.
  useEffect(() => {
    if (!open) return;

    previousFocusRef.current = document.activeElement as HTMLElement | null;

    const focusTimer = setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const candidates = panel.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (candidates[0]) candidates[0].focus();
    }, 50);

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (dismissible) onCloseRef.current();
        return;
      }
      if (e.key === "Tab" && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener("keydown", onKeyDown);

    return () => {
      clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      const prev = previousFocusRef.current;
      if (prev && document.body.contains(prev)) prev.focus();
    };
  }, [open, dismissible]);

  // mobileFullScreen disables drag (no handle) and overrides the bottom-sheet
  // shape with a full-viewport panel — see `containerClass` below.
  // Non-dismissible sheets also suppress the handle and the drag gesture
  // since both exist purely to communicate "you can swipe me away".
  const effectiveHideHandle = hideHandle || mobileFullScreen || !dismissible;
  const dragDisabled = effectiveHideHandle || isDesktop || !dismissible;
  const { motionDragProps, handleProps, bodyHandoffProps } = useDragToDismiss({
    onDismiss: onClose,
    disabled: dragDisabled,
  });

  if (typeof document === "undefined") return null;

  const variants = isDesktop ? dialogVariants : bottomSheetVariants;
  const containerClass = isDesktop
    ? desktop === "float-right"
      ? sheetClasses.floatRight
      : cn(sheetClasses.dialogPanel, sheetWidthClass(width))
    : mobileFullScreen
      ? // Full-screen mobile panel — overrides the bottom-sheet shape.
        // Bottom safe-area is owned by the footer when present (its pb-[max(env,...)])
        // already clears the gesture bar). When no footer, panel reserves it itself
        // so consumers don't need to bake safe-area into bodyClassName.
        cn(
          "fixed inset-0 flex h-[100dvh] flex-col overflow-hidden border-0 bg-card pt-[env(safe-area-inset-top)]",
          !footer && "pb-[env(safe-area-inset-bottom)]",
        )
      : // Bottom-sheet (mobile, non-fullscreen) — mirror the mobileFullScreen
        // pattern: the panel reserves bottom safe-area ONLY when there's no
        // footer. With a footer, the footer's own `pb-[max(env,...)]` already
        // clears the home indicator, so panel-level pb would double it.
        cn(sheetClasses.bottomSheet, !footer && "pb-[env(safe-area-inset-bottom)]");

  const resolvedAriaLabel =
    ariaLabel ?? (typeof title === "string" ? title : undefined) ?? "Dialog";

  const showHeader = !!headerSlot || !!title || !!description || !!headerActions;
  const showHandle = !isDesktop && !effectiveHideHandle;

  // We render directly into document.body via createPortal — base-ui's
  // DialogPrimitive.Root used to wrap this subtree but it tracks descendants
  // via React context, which createPortal escapes. With no registered Popup
  // descendant, base-ui treated EVERY click as "outside" and dismissed the
  // sheet on any tap inside the panel. The manual focus trap + body scroll
  // lock + ESC handler + backdrop click already provide every behavior the
  // primitive was supposed to add, so the primitive is dropped.
  return (
    <>
      {createPortal(
        <AnimatePresence>
          {open && (
            <>
              {showBackdrop && (
                <m.div
                  key="backdrop"
                  variants={backdropVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  onClick={dismissible ? onClose : undefined}
                  aria-hidden
                  className={sheetClasses.backdrop}
                  style={{ zIndex: backdropZ }}
                />
              )}
              <m.div
                key="panel"
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={resolvedAriaLabel}
                variants={variants}
                initial="hidden"
                animate="visible"
                exit="exit"
                {...(motionDragProps ?? {})}
                className={cn(containerClass, panelClassName)}
                style={{ zIndex: panelZ }}
              >
                {showHandle && (
                  <div className={sheetClasses.handleWrap} {...(handleProps ?? {})}>
                    <div aria-hidden className={sheetClasses.handlePill} />
                  </div>
                )}

                {showHeader &&
                  (headerSlot ?? (
                    <div
                      className={
                        showHandle ? sheetClasses.headerWrap : sheetClasses.headerWrapNoHandle
                      }
                    >
                      <div className={sheetClasses.headerTitleColumn}>
                        {title && <div className={sheetClasses.headerTitle}>{title}</div>}
                        {description && (
                          <div className={sheetClasses.headerDescription}>{description}</div>
                        )}
                      </div>
                      {headerActions}
                      {!hideCloseButton && dismissible && (
                        <button
                          type="button"
                          onClick={onClose}
                          aria-label={resolvedCloseLabel}
                          className={sheetClasses.closeButton}
                        >
                          <X className={sheetClasses.closeIcon} />
                        </button>
                      )}
                    </div>
                  ))}

                <div className={cn(sheetClasses.body, bodyClassName)} {...(bodyHandoffProps ?? {})}>
                  {children}
                </div>

                {footer && <div className={sheetClasses.footer}>{footer}</div>}
              </m.div>
            </>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}
